import {
  Children,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

interface OverflowState {
  visibleCount: number
  isOverflowing: boolean
}

interface UseOverflowTabsOptions {
  children?: ReactNode
  count?: number
  minVisible?: number
  moreButtonWidth?: number
}

export interface UseOverflowTabsResult {
  containerRef: React.RefObject<HTMLDivElement | null>
  measurerRef: React.RefObject<HTMLDivElement | null>
  state: OverflowState
}

export function useOverflowTabs({
  children,
  count,
  minVisible = 1,
  moreButtonWidth = 80,
}: UseOverflowTabsOptions): UseOverflowTabsResult {
  const itemCount = count ?? Children.count(children)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const measurerRef = useRef<HTMLDivElement | null>(null)
  const [state, setState] = useState<OverflowState>({
    visibleCount: itemCount,
    isOverflowing: false,
  })

  const calculateVisibleCount = useCallback(() => {
    const container = containerRef.current
    const measurer = measurerRef.current
    if (!container || !measurer) {
      return
    }

    const containerWidth = container.offsetWidth
    const childElements = Array.from(measurer.children) as HTMLElement[]
    const childCount = childElements.length

    if (childCount === 0) {
      setState({ visibleCount: 0, isOverflowing: false })
      return
    }

    const gap = Number.parseFloat(getComputedStyle(measurer).gap) || 0
    let cumulativeWidth = 0
    let visibleCount = 0

    for (let i = 0; i < childCount; i++) {
      const childWidth = childElements[i].offsetWidth
      const widthWithGap = i === 0 ? childWidth : childWidth + gap
      const nextCumulative = cumulativeWidth + widthWithGap

      const isLast = i === childCount - 1
      const spaceNeeded = isLast
        ? nextCumulative
        : nextCumulative + gap + moreButtonWidth

      if (spaceNeeded <= containerWidth) {
        cumulativeWidth = nextCumulative
        visibleCount++
      } else {
        break
      }
    }

    visibleCount = Math.max(visibleCount, minVisible)
    const isOverflowing = visibleCount < childCount

    setState({ visibleCount, isOverflowing })
  }, [minVisible, moreButtonWidth])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    calculateVisibleCount()

    const observer = new ResizeObserver(() => {
      calculateVisibleCount()
    })
    observer.observe(container)

    return () => observer.disconnect()
  }, [calculateVisibleCount])

  useEffect(() => {
    calculateVisibleCount()
  }, [children, count, calculateVisibleCount])

  return { containerRef, measurerRef, state }
}
